import { type DocumentData, type DocumentReference } from "firebase/firestore"

import type { FunctionComponent, ReactNode } from "react"

export type Obj = Record<string, unknown>
export type ReactChild = ReactNode
export type Component<T> = FunctionComponent<T & ReactChild>

export type Uuid = string
export type Email = string
export type PhoneNumber = string

export type FirebaseRefPath = string

export type FirebaseDocRef<T extends DocumentData> = DocumentReference<T>

export interface DataForDisplay {
  id: Uuid
  label: string
}

// Per-table sort preference, keyed by SETTINGS_KEYS value (e.g. "tryouts").
export type TableSortSetting = {
  field: string
  order: "ascend" | "descend"
}

// Quality Check wall scope: everything, non-competition pieces only, or
// competition entries only (which also switches the wall to judging order).
export type QualityCheckScope = "all" | "artwork" | "competition"

// Persisted Quality Check filters. One curriculum (plan) drives the read.
// Weekday and teacher narrow that plan. Null curriculum loads nothing.
export type QualityCheckFiltersSetting = {
  scope: QualityCheckScope
  // A semester ("2026 C") or the SemesterOptions "All" sentinel. Scopes the
  // artwork shown; defaults to the current semester.
  semester: string
  curriculumId: string | null
  weekday: number | null
  teacherId: string | null
  // Drop decided images inside a class that still has open work. Default on.
  hideFinishedItems: boolean
  // Omit a class section once every image in it is decided. Default off.
  hideFinishedClasses: boolean
}

// Standout Review dashboard — how the moment stream is grouped.
export type StandoutReviewGroupBy =
  | "class"
  | "teacher"
  | "student"
  | "day"
  | "level"

// Persisted Standout Review filters. Flat: the page is one semester-scoped
// feed, so the axes carry across. The old reaction radio and skill-level
// filter are gone; a stored `reaction` or `level` is ignored by the parser.
export type StandoutReviewFiltersSetting = {
  // A semester ("2026 C") — scopes the moments subscription itself.
  semester: string
  groupBy: StandoutReviewGroupBy
  teacherId: string | null
  classId: string | null
  // StandoutCategory ("thumbsUp" | "needsWork" | "secret") or null = All.
  category: string | null
  // Curriculum id, or null = every curriculum.
  curriculumId: string | null
  // Drop decided students inside a class that still has open work. Default on.
  hideFinished: boolean
  // Omit a class section once every card in it is decided. Default off.
  hideFinishedClasses: boolean
}

export type UserSettings = {
  theme?: "light" | "dark"
  lastPage?: string
  loggedIn?: boolean
  selectedDate?: string

  // Controlled sort per table, keyed by SETTINGS_KEYS value.
  tableSorts?: Record<string, TableSortSetting>

  // Quality Check wall filters (scope global, class axes per level).
  qualityCheckFilters?: QualityCheckFiltersSetting

  // Standout Review dashboard filters.
  standoutReviewFilters?: StandoutReviewFiltersSetting

  // TeacherView personal-notes panel: open vs collapsed. Default closed.
  personalNotesOpen?: boolean

  classesTableSearchTerm?: string
  classesTableSemester?: string
  classesTableResultsCount?: number

  studentsTableSearchTerm?: string
  // studentsTableSemester?: string
  studentsTableResultsCount?: number

  tryoutsTableSearchTerm?: string
  tryoutsTableSemester?: string
  tryoutsTableResultsCount?: number

  makeupsTableSearchTerm?: string
  makeupsTableSemester?: string
  makeupsTableResultsCount?: number

  curriculumsTableSearchTerm?: string
  curriculumsTableSemester?: string
  curriculumsTableResultsCount?: number

  projectsTableSearchTerm?: string
  projectsTableSemester?: string
  projectsTableResultsCount?: number

  usersTableSearchTerm?: string
  usersTableResultsCount?: number
  usersTableShowDisabledUsers?: boolean

  evaluationsTableSearchTerm?: string
  evaluationsTableResultsCount?: number
  evaluationsTableSemester?: string
  evaluationsTableStatusFilter?: string[]
  evaluationsTableTeacherFilter?: string[]
  evaluationsTableApproverFilter?: string[]
  evaluationsTablePage?: number
  evaluationsTableSortByStatus?: boolean
}

export enum SETTINGS_KEYS {
  CLASSES = "classes",
  STUDENTS = "students",
  TRYOUTS = "tryouts",
  MAKEUPS = "makeups",
  CURRICULUMS = "curriculums",
  PROJECTS = "projects",
  USERS = "users",
  EVALUATIONS = "evaluations",
  SEMESTERS = "semesters",
  TAGS = "tags",
}
